import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import { Button } from "./ui";
import { findTourListingId, stepsFor } from "../lib/onboarding";

// How long a step waits for its element (a page still loading its data)
// before it shows the card in the middle of the screen instead.
export const TARGET_WAIT_MS = 4000;
const POLL_MS = 120;
const PAD = 6;
const GAP = 14;

function findTarget(testId) {
  const el = document.querySelector(`[data-testid="${testId}"]`);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  // display:none (e.g. the status strip on a phone) measures as 0×0.
  return r.width > 0 && r.height > 0 ? el : null;
}

function sameRect(a, b) {
  return a && b && a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height;
}

/**
 * Welcome dialog + guided tour. `mode` is "welcome", "tour" or null (closed).
 * The parent owns persistence: onLater / onFinish save the account state.
 */
export default function OnboardingTour({ mode, user, onStart, onLater, onFinish }) {
  const { t } = useI18n();
  const nav = useNavigate();
  const location = useLocation();
  const [steps, setSteps] = useState(null);
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);      // spotlight box, viewport px
  const [searching, setSearching] = useState(false);
  const [starting, setStarting] = useState(false);
  const cardRef = useRef(null);
  const [cardPos, setCardPos] = useState(null);

  // Resolve the step list once per tour run (needs the best GREEN listing).
  useEffect(() => {
    if (mode !== "tour") { setSteps(null); return; }
    let alive = true;
    (async () => {
      const listingId = await findTourListingId();
      if (alive) { setSteps(stepsFor(user, listingId)); setIdx(0); }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const step = steps?.[idx];

  // Go to the step's page.
  useEffect(() => {
    if (!step?.route) return;
    if (location.pathname !== step.route) nav(step.route);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step?.key]);

  // Wait for the element, then follow it while the step is shown.
  useEffect(() => {
    if (!step) return undefined;
    setRect(null);
    setSearching(true);
    let el = null;
    let raf = 0;
    const started = Date.now();
    let last = null;
    const follow = () => {
      if (!el || !el.isConnected) { el = null; setRect(null); return; }
      const r = el.getBoundingClientRect();
      const next = { top: r.top - PAD, left: r.left - PAD, width: r.width + PAD * 2, height: r.height + PAD * 2 };
      if (!sameRect(next, last)) { last = next; setRect(next); }
      raf = requestAnimationFrame(follow);
    };
    const poll = setInterval(() => {
      const found = findTarget(step.target);
      if (found) {
        clearInterval(poll);
        el = found;
        setSearching(false);
        if (el.scrollIntoView) el.scrollIntoView({ block: "center", behavior: "smooth" });
        follow();
      } else if (Date.now() - started > TARGET_WAIT_MS) {
        clearInterval(poll);
        setSearching(false);
      }
    }, POLL_MS);
    return () => { clearInterval(poll); cancelAnimationFrame(raf); };
  }, [step]);

  // Place the card next to the spotlight; docked at the bottom on a phone,
  // centred when there is nothing to point at.
  useLayoutEffect(() => {
    const card = cardRef.current;
    if (!card || !rect) { setCardPos(null); return; }
    const W = window.innerWidth;
    const H = window.innerHeight;
    if (W < 640) { setCardPos(null); return; }
    const cw = card.offsetWidth;
    const ch = card.offsetHeight;
    let top = rect.top + rect.height + GAP;
    let left = rect.left;
    if (top + ch > H - 8) top = rect.top - ch - GAP;
    if (top < 8) {
      top = Math.max(8, Math.min(H - ch - 8, rect.top));
      left = rect.left + rect.width + GAP;
      if (left + cw > W - 8) left = rect.left - cw - GAP;
    }
    left = Math.max(8, Math.min(W - cw - 8, left));
    setCardPos({ top, left });
  }, [rect, idx]);

  const next = useCallback(() => {
    if (!steps) return;
    if (idx < steps.length - 1) setIdx(idx + 1);
    else onFinish();
  }, [steps, idx, onFinish]);
  const prev = useCallback(() => setIdx((i) => Math.max(0, i - 1)), []);

  useEffect(() => {
    if (!mode) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); mode === "welcome" ? onLater() : onFinish(); }
      else if (mode === "tour" && e.key === "ArrowRight") { e.preventDefault(); next(); }
      else if (mode === "tour" && e.key === "ArrowLeft") { e.preventDefault(); prev(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mode, next, prev, onLater, onFinish]);

  useEffect(() => { if (mode !== "welcome") setStarting(false); }, [mode]);

  if (!mode) return null;

  if (mode === "welcome") {
    const name = user?.name || (user?.email || "").split("@")[0];
    return (
      <div className="fixed inset-0 z-[70] bg-black/75 grid place-items-center p-4" data-testid="onboarding-welcome">
        <div role="dialog" aria-modal="true" aria-labelledby="onboarding-welcome-title"
             className="w-full max-w-md bg-surface border border-zinc-700">
          <div className="p-6 flex flex-col gap-3">
            <div className="overline">{t("onboarding.welcomeEyebrow")}</div>
            <h2 id="onboarding-welcome-title" className="font-heading text-3xl font-black tracking-tight">
              {t("onboarding.welcomeTitle", { name })}
            </h2>
            <p className="text-sm text-text-secondary leading-relaxed">{t("onboarding.welcomeBody")}</p>
            <div className="mt-2 flex flex-wrap gap-2">
              <Button
                data-testid="onboarding-start"
                disabled={starting}
                onClick={() => { setStarting(true); onStart(); }}
                autoFocus
              >
                {t("onboarding.start")}
              </Button>
              <Button variant="ghost" className="border-transparent text-text-secondary" data-testid="onboarding-later" onClick={onLater}>
                {t("onboarding.later")}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // mode === "tour"
  const total = steps?.length || 0;
  const docked = typeof window !== "undefined" && window.innerWidth < 640;
  const cardStyle = cardPos && !docked ? { top: cardPos.top, left: cardPos.left } : undefined;
  const cardClass = cardStyle
    ? "fixed"
    : docked
      ? "fixed left-3 right-3 bottom-3"
      : "fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2";

  return (
    <div className="fixed inset-0 z-[70]" data-testid="onboarding-tour">
      {/* Click shield: the page stays visible but not clickable during the tour. */}
      <div className={`absolute inset-0 ${rect ? "" : "bg-black/75"}`} onClick={(e) => e.stopPropagation()} />
      {rect && (
        <div
          aria-hidden="true"
          className="fixed pointer-events-none border border-signal-green transition-all duration-300 motion-reduce:transition-none"
          style={{ ...rect, boxShadow: "0 0 0 9999px rgba(0,0,0,0.75)" }}
          data-testid="onboarding-spotlight"
        />
      )}
      {step && (
        <div
          ref={cardRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="onboarding-step-title"
          aria-live="polite"
          className={`${cardClass} w-auto sm:w-[360px] bg-surface border border-zinc-700 flex flex-col`}
          style={cardStyle}
          data-testid={`onboarding-step-${step.key}`}
        >
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-border">
            <span className="overline">
              {t("onboarding.stepOf", { n: idx + 1, total })} · {t(`onboarding.steps.${step.key}.label`)}
            </span>
            <button
              type="button"
              onClick={onFinish}
              aria-label={t("onboarding.close")}
              data-testid="onboarding-close"
              className="p-1 text-text-muted hover:text-white"
            >
              <X size={14} />
            </button>
          </div>
          <div className="p-4 flex flex-col gap-2">
            <h3 id="onboarding-step-title" className="font-heading text-lg font-bold tracking-tight">
              {t(`onboarding.steps.${step.key}.title`)}
            </h3>
            <p className="text-sm text-text-secondary leading-relaxed">{t(`onboarding.steps.${step.key}.body`)}</p>
            <p className="font-mono text-[11px] leading-relaxed text-text-secondary border-l-2 border-signal-green pl-2">
              {t(`onboarding.steps.${step.key}.why`)}
            </p>
            {searching && <p className="font-mono text-[10px] text-text-muted">{t("onboarding.loading")}</p>}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 border-t border-border">
            <div className="flex gap-1" aria-hidden="true">
              {steps.map((s, k) => (
                <i key={s.key} className={`block w-2.5 h-[3px] ${k === idx ? "bg-white" : k < idx ? "bg-text-secondary" : "bg-zinc-700"}`} />
              ))}
            </div>
            <div className="flex gap-2">
              {idx > 0 && (
                <Button variant="ghost" className="border-transparent text-text-secondary" onClick={prev} data-testid="onboarding-prev">
                  {t("onboarding.back")}
                </Button>
              )}
              <Button onClick={next} data-testid="onboarding-next" autoFocus>
                {idx === total - 1 ? t("onboarding.done") : t("onboarding.next")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
